import React, { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, Search } from "lucide-react";

/* =========================================================================
 *  FlyerGallery — full-screen image viewer.
 *
 *  Opens from the event hero ("View Flyers"), the flyer cards in the
 *  details page, and any other "view-image" affordances. A single
 *  horizontal snap-scroller hosts the images so mobile users swipe and
 *  desktop users get prev/next chevrons. Page dots at the bottom mirror
 *  the active index.
 *
 *  Props:
 *    open          — boolean, controls visibility
 *    images        — string[] of image URLs (order matters)
 *    initialIndex  — which image to start on (defaults to 0)
 *    onClose       — close handler
 * ========================================================================= */
export default function FlyerGallery({ open, images = [], initialIndex = 0, onClose }) {
    const [index, setIndex] = useState(initialIndex);
    const scrollerRef = useRef(null);

    // Reset index every open + snap the scroller to the initial slide.
    // We do this AFTER paint so offsetWidth is settled — calling
    // scrollTo in the render pass scrolls a width-0 element to 0.
    useEffect(() => {
        if (!open) return;
        setIndex(initialIndex);
        requestAnimationFrame(() => {
            const el = scrollerRef.current;
            if (!el) return;
            el.scrollLeft = initialIndex * el.offsetWidth;
        });
    }, [open, initialIndex]);

    // Lock body scroll while open. The class survives multiple modal
    // stacks (mobile menu, success modal) because they each manage
    // their own counter via inline class — here a simple add/remove
    // is fine because Dialog is full-screen and exclusive.
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    // ESC + arrow keys for keyboard navigation. Mirrors the chevron
    // buttons; the scroll listener still owns updating `index`.
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (e.key === "Escape") onClose?.();
            if (e.key === "ArrowRight") scrollBy(1);
            if (e.key === "ArrowLeft") scrollBy(-1);
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [open]);

    if (!open || !images.length) return null;

    const scrollBy = (dir) => {
        const el = scrollerRef.current;
        if (!el) return;
        el.scrollBy({ left: dir * el.offsetWidth, behavior: "smooth" });
    };

    const onScroll = (e) => {
        const el = e.currentTarget;
        const next = Math.round(el.scrollLeft / el.offsetWidth);
        if (next !== index && next >= 0 && next < images.length) {
            setIndex(next);
        }
    };

    return (
        <div className="fixed inset-0 z-[400] bg-black/95 flex flex-col">
            {/* Top bar with close button */}
            <div className="absolute top-0 right-0 z-50 p-4 sm:p-6 flex justify-end w-full bg-gradient-to-b from-black/50 to-transparent">
                <button
                    type="button"
                    onClick={onClose}
                    className="w-[48px] h-[48px] bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full transition-colors text-white shadow-lg border-none"
                    aria-label="Close"
                >
                    <X size={24} />
                </button>
            </div>

            {/* Slides + side chevrons (desktop) */}
            <div className="flex-1 flex items-center justify-center relative w-full h-full overflow-hidden">
                {images.length > 1 && (
                    <button
                        type="button"
                        onClick={() => scrollBy(-1)}
                        disabled={index === 0}
                        className="hidden md:flex absolute left-6 z-20 p-4 bg-white/10 hover:bg-white/20 disabled:opacity-0 backdrop-blur-md rounded-full transition-all text-white shadow-lg"
                        aria-label="Previous image"
                    >
                        <ChevronLeft size={36} strokeWidth={2.5} />
                    </button>
                )}

                <div
                    ref={scrollerRef}
                    onScroll={onScroll}
                    className="flex w-full h-full overflow-x-auto snap-x snap-mandatory items-center"
                    style={{ scrollbarWidth: "none" }}
                >
                    {images.map((src, i) => (
                        <div
                            key={i}
                            className="w-full h-full shrink-0 snap-center flex items-center justify-center p-4 md:p-20 relative"
                        >
                            <img
                                src={src}
                                alt={`Flyer ${i + 1}`}
                                draggable={false}
                                className="max-w-full max-h-full object-contain drop-shadow-2xl select-none"
                            />
                        </div>
                    ))}
                </div>

                {images.length > 1 && (
                    <button
                        type="button"
                        onClick={() => scrollBy(1)}
                        disabled={index === images.length - 1}
                        className="hidden md:flex border-none absolute right-6 z-20 w-[68px] h-[68px] flex justify-center items-center bg-white/10 hover:bg-white/20 disabled:opacity-0 backdrop-blur-md rounded-full transition-all text-white shadow-lg"
                        aria-label="Next image"
                    >
                        <ChevronRight size={36} strokeWidth={2.5} />
                    </button>
                )}
            </div>

            {/* Bottom page dots */}
            {images.length > 1 && (
                <div className="absolute bottom-8 left-0 right-0 flex justify-center space-x-3 z-50 bg-gradient-to-t from-black/50 to-transparent pt-10 pb-4 pointer-events-none">
                    {images.map((_, i) => (
                        <div
                            key={i}
                            className={`w-2.5 h-2.5 rounded-full transition-all ${
                                i === index
                                    ? "bg-[#ffea00] scale-125 shadow-sm"
                                    : "bg-white/40"
                            }`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------------
 *  ViewFlyersChip — small chip rendered over a clickable hero/flyer
 *  image to telegraph the gallery interaction. Lives here so the icon
 *  + styling stays in one place.
 * ------------------------------------------------------------------------- */
export function ViewFlyersChip() {
    return (
        <div className="bg-white/95 backdrop-blur-md px-5 py-3 rounded-2xl opacity-0 group-hover:opacity-100 transform scale-95 group-hover:scale-100 transition-all duration-300 shadow-2xl text-black flex items-center gap-2 font-black text-sm">
            <Search size={18} /> View Flyers
        </div>
    );
}
